"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { looksLikeBinary } from "@/lib/tool-kit/developer/encoding-detect";

type Direction = "auto" | "to-binary" | "to-text";

const BYTE_GROUP_REGEX = /^[01]{8}$/;
const WHITESPACE_REGEX = /\s+/;

type Strings = { [K in keyof typeof BINARY_CODE_TRANSLATOR_STRINGS]: string };

const DIRECTIONS: { value: Direction; label: keyof Strings }[] = [
  { value: "auto", label: "directionAuto" },
  { value: "to-binary", label: "directionToBinary" },
  { value: "to-text", label: "directionToText" },
];

function encodeToBinary(text: string): {
  output: string;
  bytes: number;
  error: string;
} {
  const encoded = new TextEncoder().encode(text);
  const groups: string[] = [];
  for (const byte of encoded) {
    groups.push(byte.toString(2).padStart(8, "0"));
  }
  return { output: groups.join(" "), bytes: encoded.length, error: "" };
}

function decodeFromBinary(
  binary: string,
  strings: Strings
): {
  output: string;
  bytes: number;
  error: string;
} {
  const groups = binary.trim().split(WHITESPACE_REGEX);
  const invalid = groups.filter((group) => !BYTE_GROUP_REGEX.test(group));
  if (invalid.length > 0) {
    return {
      output: "",
      bytes: 0,
      error: t(strings.errorInvalidGroups, {
        groups: `${invalid.slice(0, 5).join(", ")}${invalid.length > 5 ? "…" : ""}`,
      }),
    };
  }
  const byteValues = groups.map((group) => Number.parseInt(group, 2));
  const output = new TextDecoder().decode(new Uint8Array(byteValues));
  return { output, bytes: byteValues.length, error: "" };
}

function translate(
  input: string,
  direction: Direction,
  strings: Strings
): {
  output: string;
  bytes: number;
  error: string;
  decoded: boolean;
} {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { output: "", bytes: 0, error: "", decoded: false };
  }
  const shouldDecode =
    direction === "to-text" ||
    (direction === "auto" && looksLikeBinary(trimmed));
  if (shouldDecode) {
    return { ...decodeFromBinary(trimmed, strings), decoded: true };
  }
  return { ...encodeToBinary(input), decoded: false };
}

/** English strings. A translated content file overrides these through `ui`. */
export const BINARY_CODE_TRANSLATOR_STRINGS = {
  direction: "Direction",
  textOrBinary: "Text or binary",
  hiOr0100100001101001: "Hi  —or—  01001000 01101001",
  decodedText: "Decoded text",
  binary: "Binary",
  byte: "one: byte|other: bytes",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  directionAuto: "Auto-detect",
  directionToBinary: "Text → Binary",
  directionToText: "Binary → Text",
  errorInvalidGroups:
    "Every group must be exactly 8 binary digits. Invalid: {groups}",
} as const;

export default function BinaryCodeTranslatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("auto");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(BINARY_CODE_TRANSLATOR_STRINGS);
  const { output, bytes, error, decoded } = translate(
    input,
    direction,
    strings
  );

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const fmt = useToolNumber();

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {strings[d.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="binary-input"
        >
          {strings.textOrBinary}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="binary-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.hiOr0100100001101001}
          value={input}
        />
      </div>
      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="binary-output"
          >
            {decoded ? strings.decodedText : strings.binary}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="binary-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {fmt(bytes)}
            {tPlural(strings.byte, locale, bytes)}
          </p>
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
